import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { LogLevel, HttpTransportType } from "@microsoft/signalr";
import {
    GridRenderCellParams, GridCellEditStopReasons, GridCellEditStopParams,
    GridValueFormatter
} from "@mui/x-data-grid";
import { DataGridPro, GridColDef, GridColumnOrderChangeParams, GridPaginationModel, GridRowParams, GridRowId } from "@mui/x-data-grid-pro";

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Typography,
    //Box,
} from "@mui/material";
//import { GridFilterInputDate } from "@mui/x-data-grid/components/panel/filterPanel/GridFilterInputDate";

import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

import dayjs from 'dayjs';
//import { format }  from "date-fns";
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/zh-cn';
import { DatePicker } from "@mui/x-date-pickers"; 
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useTranslation } from "react-i18next";

const locales = ['en', 'en-gb', 'zh-cn', 'de'];
type LocaleKey = (typeof locales)[number];

import axiosExt from '../axios';
import { Order, Customer, User,OrderType, OrderState, Service , OrderService} from "../types";
import { API_BASE_URL } from "../config.ts";

//import { LicenseInfo } from '@mui/x-license-pro';
//LicenseInfo.setLicenseKey('b1a2b5992264fefb50983aca320ec56cTz0xMDkzNDgsRT0xNzczMTAwNzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

interface Props {
    customerId?: string | null;
}

const OrderTable: React.FC<Props> = ({ customerId }) => {
    const navigate = useNavigate();
    const [locale, setLocale] = React.useState<LocaleKey>('de');
    const { t } = useTranslation();

    const [orders, setOrders] = useState<Order[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState<Record<string, boolean>>({});
    const [open, setOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [users, setUsers] = useState<User[]>([]);
    const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);
    const [orderStates, setOrderStates] = useState<OrderState[]>([]);
    const [services, setServices] = useState<Service[]>([]);

    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    // ✅ Track Previous Values to Avoid Redundant Calls
    const prevPage = useRef<number | null>(null);
    const prevPageSize = useRef<number | null>(null);
    const prevSortModel = useRef<GridSortModel | null>(null);
    const prevFilterModel = useRef<GridFilterModel | null>(null);

    const formatDate: GridValueFormatter = (value : string) => {
        if (!value || typeof value !== "string") return "";

        // Ensure the timestamp is treated as UTC
        const isoString = value.endsWith("Z") ? value : `${value}Z`;
        const date = new Date(isoString);

        if (isNaN(date.getTime())) return "";

        const dateLocal = new Intl.DateTimeFormat(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }).format(date);

        return dateLocal;
    };


    const fetchServices = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get<Service[]>(`${API_BASE_URL}/services`, {
                headers: { "Authorization": `Bearer ${token}` },
            });
            setServices(response.data);
        } catch (error) {
            console.error("Error fetching services:", error);
        }
    };

    const fetchOrderTypes = async () => {
        setLoading((prev) => ({ ...prev, global: true }));

        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get<OrderType[]>(`${API_BASE_URL}/orderTypes/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
            );
            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }

            setOrderTypes(response.data);
        } catch (error) {
            console.error("Error fetching orderTypes:", error);
        } finally {
            setLoading((prev) => ({ ...prev, global: false }));
        }
    }

    const fetchOrderStates = async () => {
        setLoading((prev) => ({ ...prev, global: true }));

        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get<OrderState[]>(`${API_BASE_URL}/orderStates/`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
            );
            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }

            setOrderStates(response.data);
        } catch (error) {
            console.error("Error fetching order states:", error);
        } finally {
            setLoading((prev) => ({ ...prev, global: false }));
        }
    }

    const fetchUsers = async () => {
        setLoading((prev) => ({ ...prev, global: true }));

        try {
            const token = localStorage.getItem("token");
            const response = await axiosExt.get<User[]>(`${API_BASE_URL}/users/roles/admin-support`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
            );
            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }
            console.log("1 Setting users to:", response.data);
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading((prev) => ({ ...prev, global: false }));
            //console.error(users.length);
        }
    }

    /*const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await axiosExt.get("https://localhost:5001/api/orders", {
                params: {
                    page,
                    pageSize,
                    sortField,
                    sortOrder,
                    filterField,
                    filterValue,
                },
            });

            setOrders(response.data.data);
            setTotalRows(response.data.totalRecords);
        } catch (error) {
            console.error("Error fetching orders:", error);
        }
        setLoading(false);
    };*/

    const fetchCustomers = async () => {
        const token = localStorage.getItem("token");

        try {
            const response = await axiosExt.get<Customer[]>(`${API_BASE_URL}/customers`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }

            );

            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }

            setCustomers(response.data);
        } catch (error) {
            console.error("Error fetching customers:", error);
        }
    };

    // Base columns do lookups referencing the latest users, orders, etc.
    const baseColumns = useMemo<GridColDef[]>(() => {
        return [
            {
                field: "actions",
                headerName: t("Actions"),
                width: 300,
                renderCell: (params: GridRenderCellParams) => (
                    <>
                        <Button
                            variant="contained"
                            onClick={() => handleEdit(params.row as Order)}
                            style={{ marginRight: "10px" }}
                            size="small"
                        >
                            <EditIcon />
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/orderdetailspage/${params.row.id}`)}
                            style={{ marginRight: "10px" }}
                        >
                            <VisibilityIcon />
                        </Button>
                        < Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleDelete(params.row.id)}
                        >
                            <DeleteIcon />
                        </Button>
                    </>

                ),
            },
            {
                field: "hasUnreadEmails",
                headerName: t("New Email"),
                width: 80,
                align: "center",
                headerAlign: "center",
                sortable: false,
                renderCell: (params) => params.value ? <MailOutlineIcon color="primary" /> : null,
            },
            { field: "internalNumber", headerName: t("Internal Number"), width: 100 },
            {
                field: "createdDateTime",
                headerName: t("Created On"),
                type: "dateTime",
                width: 180,
                valueGetter: (_, row) => {
                    return `${row.createdDateTime || ''}`;
                },

                // ✅ Format the Date object safely
                valueFormatter: formatDate,

                renderCell: (params) => {
                    if (!params.value || typeof params.value !== "string") return "";

                    // Add 'Z' if missing to force UTC interpretation
                    const isoString = params.value.endsWith("Z") ? params.value : `${params.value}Z`;
                    const date = new Date(isoString);
                    if (isNaN(date.getTime())) return "";

                    const dateLocal = new Intl.DateTimeFormat(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    }).format(date);

                    return dateLocal;
                }

            },
            {
                field: "assignedToUserId", headerName: t("Assigned To"), width: 100, valueGetter: (params) => {
                    //console.log("Search users:", users);
                    //const userId = params as string;
                    const user = users.find(u => u.id === params);
                    return user ? user.userName : '';
                },
            },
            { field: "initialEmailSubject", headerName: t("Initial Email Subject"), width: 100 },
            { field: "initialEmailSubjectShorten", headerName: "Shorten Email Subject", width: 100 },
            {
                field: "customerId", headerName: t("Customer"), width: 100, valueGetter: (params) => {
                    const customer = customers.find(u => u.id === params);
                    return customer ? customer.name : '';
                }
            },
            {
                field: "stateId", headerName: t("State"), width: 100, valueGetter: (params) => {
                    const orderState = orderStates.find(u => u.id === params);
                    return orderState ? orderState.name : '';
                }
            },
            {
                field: "orderTypeId", headerName: t("Type"), width: 100, valueGetter: (params) => {
                    const orderType = orderTypes.find(u => u.id === params);
                    return orderType ? orderType.name : '';
                }
            },
            {
                field: "services",
                headerName: t("Services"),
                width: 200,
                renderCell: (params: GridRenderCellParams) => {
                    const orderServices: OrderService[] = params.row.orderServices ?? []; // ✅ Extract `OrderServices`

                    // ✅ Extract service IDs from OrderServices
                    const serviceIds = orderServices.map(os => os.serviceId);

                    // ✅ Match service names based on extracted IDs
                    const serviceNames = services
                        .filter(service => serviceIds.includes(service.id ?? ""))
                        .map(service => service.name)
                        .join(", ");

                    return <span>{serviceNames || t("No services")}</span>;
                }
            },
            {
                field: "dueDate", headerName: t("Due Date"), width: 100, type: 'date',
                valueFormatter: (params) => {
                    if (params == null) {
                        return "";
                    }
                    else {
                        const { year, month, day } = params;
                        const date = new Date(year, month - 1, day);
                        return new Intl.DateTimeFormat(locale, {
                            dateStyle: 'short',
                        }).format(date);
                        return "";
                    }

                }
            },
            {
                field: "prePaid", headerName: t("Pre Paid"), width: 100,
                valueFormatter: (params) => {
                    if (params == null) {
                        return t("no");
                    }
                    else {
                        if (params == true) { return t("yes"); }
                        else { return t("no"); }
                    }
                }
            },
            {
                field: "invoiced", headerName: t("Invoiced"), width: 100,
                valueFormatter: (params) => {
                    if (params == null) {
                        return t("no");
                    }
                    else {
                        if (params == true) { return t("yes"); }
                        else { return t("no"); }
                    }
                }
            },
            {
                field: "paid", headerName: t("Paid"), width: 100,
                valueFormatter: (params) => {
                    if (params == null) {
                        return t("no");
                    }
                    else {
                        if (params == true) { return t("yes"); }
                        else { return t("no"); }
                    }
                }
            },
            {
                field: "invoiceNumber", headerName: t("Invoice Number"), width: 100, editable: false,
            },
            {
                field: "notes", headerName: t("Notes"), width: 100, editable: false,
            },
            { field: "missingInfo", headerName: t("Missing Info"), width: 100, editable: false},
            {
                field: "documentStored", headerName: t("Documents Stored"), width: 100,
                valueFormatter: (params) => {
                    if (params == null) {
                        return t("no");
                    }
                    else {
                        if (params == true) { return t("yes"); }
                        else { return t("no"); }
                    }
                }
            },

        ];
    } , [t, locale, users, customers, orderStates, orderTypes, services]);

    const [columns, setColumns] = useState<GridColDef[]>(() => {
        // On initial load, you could parse localStorage here if you wish
        return baseColumns;
    });
    //const [error, setError] = useState(false); // Validation error state

    const savedSortKey = "sortOrdersList";
    const savedFilterKey = "filterOrdersList";
    const savedColumnsOrderKey = 'columnOrdersList';

    const fetchOrders = useCallback(async () => {
        setLoading((prev) => ({ ...prev, global: true }));

        try {
            const sortField = sortModel.length > 0 ? sortModel[0].field : "Id";
            const sortOrder = sortModel.length > 0 ? sortModel[0].sort : "asc";
            //const filterItem: GridFilterItem = filterModel.items.length > 0 ? filterModel.items[0] : {};
            //const filterField = filterItem.columnField || "";
            //const filterValue = filterItem.value || "";

            // Extract multiple filters
            const filterFields = filterModel.items.map(f => f.field);
            const filterValues = filterModel.items.map(f => f.value);
            const filterOperations = filterModel.items.map(f => f.operator);

            const token = localStorage.getItem("token");
            /*const searchParam = customerId ? `search=${customerId}` : "";
            const response = await axiosExt.get<Order[]>(`${API_BASE_URL}/orders?${searchParam}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            }
            );
            if (response.status === 401) {
                throw new Error("Unauthorized. Please log in again.");
            }
            setOrders(response.data);*/
            //console.log("Sorting by:", sortField);
            const response = await axiosExt.get(`${API_BASE_URL}/orders?`, {
                params: {
                    page,
                    pageSize,
                    sortField,
                    sortOrder,
                    filterFields,
                    filterOperations,
                    filterValues,
                },
                paramsSerializer: params => new URLSearchParams(params).toString(),
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            //setOrders(response.data.data);
            setOrders((response.data.data as Order[]).map((order: Order) => ({
                ...order,
                id: order.id ?? `temp-${order.internalNumber || crypto.randomUUID()}`
            })));
            setTotalRows(response.data.totalRecords);

        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading((prev) => ({ ...prev, global: false }));
        }

    }, [page, pageSize, sortModel, filterModel]);  // Dependencies to prevent infinite loop

    useEffect(() => {
        // Whenever baseColumns changes (like after 'users' updates),
        // we merge the *ordering* in `columns` with the *callbacks* in `baseColumns`.
        setColumns(prevColumns => {
            // 1) Build a map from field -> new base column
            const baseMap = new Map(baseColumns.map(col => [col.field, col]));

            // 2) Rebuild an array preserving user’s *order* from `prevColumns`,
            //    but injecting the updated definitions from baseColumns.
            const merged = prevColumns.map(oldCol => {
                // If baseColumns still has that field, use the updated column
                if (baseMap.has(oldCol.field)) {
                    const updatedCol = baseMap.get(oldCol.field)!;
                    baseMap.delete(oldCol.field); // So we don't reuse it
                    return { ...updatedCol, width: oldCol.width };
                }
                // else, it might have been removed from baseColumns – skip or keep oldCol
                return oldCol;
            });

            // 3) If there are any new columns in baseColumns that
            //    didn't exist before, add them at the end
            for (const leftover of baseMap.values()) {
                merged.push(leftover);
            }

            return merged;
        });
    }, [baseColumns]);

    useEffect(() => {
        const isFirstLoad = prevPage.current === null;

        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        const savedColumnOrder = localStorage.getItem(savedColumnsOrderKey);
        if (savedColumnOrder) {
            try {
                const orderedFields = JSON.parse(savedColumnOrder);
                if (Array.isArray(orderedFields)) {
                    const reorderedColumns = orderedFields
                        .map((field) => columns.find((col) => col.field === field))
                        .filter((col): col is GridColDef => col !== undefined);
                    setColumns(reorderedColumns);
                }
            } catch (e) {
                console.error('Error parsing stored column order:', e);
            }
        }

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE_URL}/orderHub`, {
                transport: HttpTransportType.WebSockets, // ✅ Force WebSockets to avoid negotiation issues
                withCredentials: true // ✅ Required if using authentication
            })
            .configureLogging(LogLevel.Debug)
            .withAutomaticReconnect()
            .build();

        // Start the connection
        async function startConnection() {
            if (connection.state === signalR.HubConnectionState.Connected || connection.state === signalR.HubConnectionState.Connecting) {
                //console.log("SignalR already connected or connecting...");
                return;
            }

            try {
                await connection.start();
                //console.log("Connected to SignalR Hub");
            } catch (err) {
                console.error("Error connecting to SignalR:", err);
                //console.error(API_BASE_URL);
                setTimeout(startConnection, 5000); // Retry connection if it fails
            }
        }

        startConnection();

        // Listen for new order creation
        connection.on("NewOrderCreated", (newOrderId) => {
            console.log("New order created:", newOrderId);
            fetchOrders(); // Refresh orders without refreshing page
        });

        // Listen for order updates
        connection.on("OrderHasNewEmail", (updatedOrderId) => {
            //console.log("Order has new email:", updatedOrderId);
            setOrders((prevOrders) =>
                prevOrders.map(o =>
                    o.id === updatedOrderId ? { ...o, hasUnreadEmails: true } : o
                )
            );
        });

        setLocale('de');
        console.log("Available locales:", locales);

        fetchUsers();
        fetchServices();
        fetchOrderTypes();
        fetchOrderStates();
        fetchCustomers();
        //fetchOrders();
        if (
            isFirstLoad ||
            prevPage.current !== page ||
            prevPageSize.current !== pageSize ||
            JSON.stringify(prevSortModel.current) !== JSON.stringify(sortModel) ||
            JSON.stringify(prevFilterModel.current) !== JSON.stringify(filterModel)
        ) {
            fetchOrders();
            // **Update Ref Values**
            prevPage.current = page;
            prevPageSize.current = pageSize;
            prevSortModel.current = sortModel;
            prevFilterModel.current = filterModel;
        }

    }, [customerId]);
    //        , [customerId]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]); // ✅ Using useCallback function

    useEffect(() => {
        setSelectionModel([]); // Clear previous selection
    }, [orders]); // 


    useEffect(() => {
        console.log("users changed ->", users);
    }, [users]);

    useEffect(() => {
        const savedColumnOrderStr = localStorage.getItem("myColumnOrderKey");
        if (!savedColumnOrderStr) return;

        try {
            const savedOrder = JSON.parse(savedColumnOrderStr) as string[];
            setColumns(prev => {
                // Rebuild array in that order
                const newCols: GridColDef[] = [];
                for (const field of savedOrder) {
                    const found = prev.find(col => col.field === field);
                    if (found) newCols.push(found);
                }
                // Add any columns that weren't in local storage
                for (const col of prev) {
                    if (!newCols.includes(col)) {
                        newCols.push(col);
                    }
                }
                return newCols;
            });
        } catch (err) {
            console.error("Error parsing column order:", err);
        }
    }, []);

    // Generate dynamic `sx` styles for each orderState
    /*const dynamicSx = orderStates.reduce((styles, state) => {
        styles[`& .row-state-${state.id}`] = {
            backgroundColor: state.bgColorRGB,
            color: state.colorRGB,
        };
        return styles;
    }, {});*/
    const dynamicSx: Record<string, { backgroundColor: string; color: string; }> =
        orderStates.reduce((styles, state) => {
            styles[`& .row-state-${state.id}`] = {
                backgroundColor: state.bgColorRGB,
                color: state.colorRGB,
            };
            return styles;
        }, {} as Record<string, { backgroundColor: string; color: string; }>);


    /*const getCreatedDateTime: GridValueGetter<{ createdDateTime?: string | Date | null }> = (_, row) => {
        return row.createdDateTime ? new Date(row.createdDateTime) : null;
    };*/

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage with Date Formatting
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        const formattedModel = {
            ...newModel,
            items: newModel.items.map((filter) => {
                if ((filter.field === "dueDate") && filter.value) {
                    return {
                        ...filter,
                        value: new Date(filter.value as string).toISOString().split("T")[0], // Convert to YYYY-MM-DD
                    };
                }
                if ((filter.field === "createdDateTime") && filter.value) {
                    const date = new Date(filter.value as string);

                    // ✅ Preserve Local Time Instead of UTC
                    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;

                    return {
                        ...filter,
                        value: formattedDate, // ✅ Correct local date-time format (YYYY-MM-DD HH:mm:ss)
                    };
                }
                return filter;
            }),
        };

        setFilterModel(formattedModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(formattedModel));
    };

    // Handle column order change
    const handleColumnOrderChange = useCallback((params: GridColumnOrderChangeParams) => {
        const { column, targetIndex } = params;

        setColumns(oldCols => {
            const newCols = [...oldCols];
            const oldIndex = newCols.findIndex((c) => c.field === column.field);
            if (oldIndex === -1) return newCols;

            // Reorder them in place
            const [moved] = newCols.splice(oldIndex, 1);
            newCols.splice(targetIndex, 0, moved);

            // Save to local storage
            const fieldsInNewOrder = newCols.map(c => c.field);
            localStorage.setItem("myColumnOrderKey", JSON.stringify(fieldsInNewOrder));

            return newCols;
        });
    }, []);

    const handleEdit = (order: Order) => {
        //setError(false); // Reset validation state
        setSelectedOrder(order);
        setOpen(true);
    };

    /*const handleDelete = async (id: string) => {
        const token = localStorage.getItem("token");

        try {
            await axiosExt.delete(`${API_BASE_URL}/orders/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setOrders(orders.filter((c) => c.id !== id));
        } catch (error) {
            console.error("Error deleting order:", error);
        }
    };*/

    const handleDelete = async (id: string) => {
        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);

        } catch (error) {
            console.error("Error deleting item:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        const token = localStorage.getItem("token");

        try {
            await axiosExt.delete(`${API_BASE_URL}/orders/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setOrders(orders.filter((c) => c.id !== itemToDelete));
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };


    const handleSave = async () => {
        const token = localStorage.getItem("token");

        /*if (!selectedOrder?.internalNumber) {
            setError(true); // Show error if the field is empty
            return;
        }*/

        const orderServices = (selectedOrder?.orderServices ?? []).map(os => ({
            serviceId: os.serviceId,
            ...(selectedOrder?.id ? { orderId: selectedOrder.id } : {}) // Only include orderId if updating
        }));


        if (selectedOrder) {
            try {
                const payload = {
                    ...selectedOrder,
                    orderServices: orderServices,  // OrderServices without empty orderId
                    serviceIds: undefined,  // Remove unnecessary serviceIds field
                };

                if (!payload?.stateId || payload.stateId === "") {
                    payload.stateId = null;
                }
                if (!payload?.orderTypeId || payload.orderTypeId === "") {
                    payload.orderTypeId = null;
                }
                if (selectedOrder.id == null || selectedOrder.id === '') {
                    if (!selectedOrder.id) {
                        //delete payload.id; // Remove the id field
                        selectedOrder.id = crypto.randomUUID();
                    }
                    // Add new order
                    const response = await axiosExt.post(`${API_BASE_URL}/orders`, payload, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    setOrders([...orders, response.data]);
                } else {
                    // Update existing order
                    await axiosExt.put(`${API_BASE_URL}/orders/${selectedOrder.id}`, payload, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    setOrders(
                        orders.map((c) =>
                            c.id === selectedOrder.id ? selectedOrder : c
                        )
                    );
                }
                setOpen(false);
                setSelectedOrder(null);
            } catch (error) {
                console.error("Error saving order:", error);
            }
        }
    };

    // Simulated API Save Function
    /*const saveNoteToDatabase = useCallback(async (id: string, notes: string) => {
        setLoading((prev) => ({ ...prev, [id]: true })); // Show loading indicator
        try {
            await axiosExt.put(`/api/orders/${id}`, { notes }); // API call to save note
            console.log(`Saved note for order ${id}:`, notes);
        } catch (error) {
            console.error("Failed to save note:", error);
        } finally {
            setLoading((prev) => ({ ...prev, [id]: false })); // Hide loading indicator
        }
    }, []);*/

    // Handle notes change
    /*const handleEditCellChange = (id: string, value: string) => {
        setOrders((prevOrders) =>
            prevOrders.map((order) => (order.id === id ? { ...order, notes: value } : order))
        );

        // Auto-save after a slight delay
        setTimeout(() => {
            saveNoteToDatabase(id, value);
        }, 500);
    };*/

    // Move to the next row when Enter is pressed
    const handleEditStop = (params: GridCellEditStopParams) => {
        if (params.reason === GridCellEditStopReasons.enterKeyDown) {
            //setEditableRow((prev) => (prev + 1 < orders.length ? prev + 1 : prev));
        }
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedOrder(null);
    };

    

    return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
    <div>
        <div style= {{ width: "100%", display: "flex", marginTop: "0px" }}>
            <h2>{ customerId? `t("Orders for Customer") ${customerId}` : t("All Orders")}</h2>
                < Button
                    variant = "contained"
                    color = "primary"
                    onClick={() => {
                        setSelectedOrder({
                            id: "",
                            customerId: customerId || "",
                            declarantCustomerId: null,
                            internalNumber: null,
                            assignedToUserId: null,
                            stateId: null,
                            priority: null, // 
                            orderTypeId: null,
                            mergedToOrderId: null,
                            hasUnreadEmails: false,
                            unreadEmailsNumber: 0,
                            lastEmailReceivedAt: null,
                            dueDate: null, // 
                            prePaid: false,
                            invoiced: false,
                            invoiceNumber: null,
                            paid: false,
                            initialEmailSubject: null,
                            initialEmailSubjectShorten: null,
                            notes: null,
                            missingInfo: null,
                            createdDateTime: new Date(),
                            documentStored: false,
                            emailConversations: null,
                            serviceIds: null,
                            orderServices: null,
                            orderDetails: null,
                            orderInvoices: null,
                            orderDetailsTaxAssessments: null,
                        });

                        
                        setOpen(true);
                    }}
                    style = {{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                                    >
                    {t("Add")}
               </Button>
                </div>
                {loading.global ? (
                    <div>{t("Loading...")}</div>
                ) : (
                    < div style={{ height: 600, width: "100%" }}>
                        <DataGridPro
                            rows={orders}
                            columns={columns}
                            sortModel={sortModel}
                            onSortModelChange={handleSortModelChange}
                            filterModel={filterModel}
                            onFilterModelChange={handleFilterModelChange}
                            rowCount={totalRows}
                            disableColumnReorder={false}
                            rowSelectionModel={selectionModel}
                            onRowSelectionModelChange={(newSelection) => setSelectionModel([...newSelection])}
                            pagination
                            paginationMode="server"
                            sortingMode="server"
                            filterMode="server"
                            onColumnOrderChange={handleColumnOrderChange}
                            onPaginationModelChange={(model: GridPaginationModel) => {
                                setPage(model.page);
                                setPageSize(model.pageSize);
                            }}
                            onCellEditStop={handleEditStop}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: 10, page: 0 },
                                },
                            }}
                            pageSizeOptions={[5, 10, 20]}
                            loading={Object.values(loading).some(Boolean)}
                            getRowId={(row: Order) => {
                                if (!row.id) {
                                    console.warn("Row missing ID! Generating fallback ID.");
                                }
                                return row.id || `temp-${row.internalNumber || crypto.randomUUID()}`;
                            }}
                            getRowClassName={(params: GridRowParams<Order>) => `row-state-${params.row.stateId}`}
                            sx={{
                                ...dynamicSx, // Apply the dynamically generated styles
                            }}
                        />
                    </div>)}
        < Dialog open = { open } onClose = { handleClose } >
            <DialogTitle>
                    {selectedOrder?.id
                        ? `${t("Edit")} ${t("Order")} # ${selectedOrder.internalNumber || "-"}`
                        : t("Add")
                    }
            </DialogTitle>
            < DialogContent >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>{t("Initial Email Subject") }: {selectedOrder?.initialEmailSubject}</Typography>
            <TextField
                margin="dense"
                label={t("Shorten Email Subject")}
                fullWidth
                value={selectedOrder?.initialEmailSubjectShorten || ""}
                onChange={(e) =>
                    setSelectedOrder((prev) =>
                        prev ? { ...prev, initialEmailSubjectShorten: e.target.value } : null
                    )}
            />

            < FormControl fullWidth margin="dense" >
                <InputLabel>{t("Customer")}</InputLabel>
                < Select
                    value={selectedOrder?.customerId || ""}
                    onChange={(e) =>
                        setSelectedOrder((prev) =>
                            prev ? { ...prev, customerId: e.target.value } : null
                        )}
                >
                    {
                        customers.map((customer) => (
                            <MenuItem key={customer.id} value={customer.id} >
                                {customer.name}
                            </MenuItem>
                        ))
                    }
                </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
                <InputLabel>{t("Assigned To")}</InputLabel>
                <Select
                    value={selectedOrder?.assignedToUserId || ""}
                    onChange={(e) =>
                        setSelectedOrder((prev) =>
                            prev ? { ...prev, assignedToUserId: e.target.value } : null
                        )
                    }
                >
                    {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                            {user.userName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
                <InputLabel>{t("State")}</InputLabel>
                <Select
                    value={selectedOrder?.stateId || ""}
                    onChange={(e) =>
                        setSelectedOrder((prev) =>
                            prev ? { ...prev, stateId: e.target.value } : null
                        )
                    }
                >
                    {orderStates.map((orderState) => (
                        <MenuItem key={orderState.id} value={orderState.id || ""}>
                            {orderState.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
                        <InputLabel>{t("Type")}</InputLabel>
                <Select
                    value={selectedOrder?.orderTypeId || ""}
                    onChange={(e) =>
                        setSelectedOrder((prev) =>
                            prev ? { ...prev, orderTypeId: e.target.value } : null
                        )
                    }
                >
                    {orderTypes.map((orderType) => (
                        <MenuItem key={orderType.id ?? ""} value={orderType.id ?? ""}>
                            {orderType.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
                <InputLabel>{t("Services")}</InputLabel>
                <Select
                    multiple
                    value={selectedOrder?.orderServices?.map(os => os.serviceId) ?? []}
                    onChange={(e) => {
                        const values = e.target.value as string[];

                        setSelectedOrder((prev) =>
                            prev
                                ? {
                                    ...prev,
                                    orderServices: values.map(serviceId => {
                                        // preserve existing data if available
                                        const existing = prev.orderServices?.find(os => os.serviceId === serviceId);

                                        return existing ?? {
                                            orderId: prev.id || "",
                                            serviceId,
                                            quantity: 1, // default if new
                                            assignedUserId: null,
                                            invoiceNotes: null,
                                            additionalUnits: 0,
                                            customPrice: 0,
                                            isCompleted: false,
                                        };
                                    }),
                                }
                                : null
                        );
                    }}
                    renderValue={(selected) =>
                        services
                            .filter(s => s.id && (selected as string[]).includes(s.id))
                            .map(s => s.name)
                            .join(", ")
                    }
                >
                    {services.map((service) => (
                        <MenuItem key={service.id ?? ""} value={service.id ?? ""}>
                            <Checkbox
                                checked={
                                    selectedOrder?.orderServices?.some(os => os.serviceId === service.id) || false
                                }
                            />
                            {service.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <div>
                <DatePicker
                    label={t("Due Date")}
                    value={
                        selectedOrder?.dueDate
                            ? dayjs(new Date(selectedOrder.dueDate.year, selectedOrder.dueDate.month - 1, selectedOrder.dueDate.day))
                            : null
                    }
                    onChange={(date) => {
                        if (date) {
                            setSelectedOrder((prev) => {
                                return prev
                                    ? {
                                        ...prev,
                                        dueDate: {
                                            year: date.year(),
                                            month: date.month()+ 1,
                                            day: date.date(),
                                            dayOfWeek: date.day(),
                                        },
                                    }
                                    : null;
                            });
                        } else {
                            setSelectedOrder((prev) => {
                                return prev ? { ...prev, dueDate: null } : null;
                            });
                        }
                    }}

                />
            </div>

            <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedOrder?.prePaid || false}
                                onChange={(e) =>
                                    setSelectedOrder((prev) =>
                                        prev ? { ...prev, prePaid: e.target.checked } : null
                                    )}
                                color="primary"
                            />
                        }
                        label={t("Pre Paid")}
            />
            <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedOrder?.invoiced || false}
                                onChange={(e) =>
                                    setSelectedOrder((prev) =>
                                        prev ? { ...prev, invoiced: e.target.checked } : null
                                    )}
                                color="primary"
                            />
                        }
                        label={t("Invoiced")}
            />
            <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedOrder?.paid || false}
                                onChange={(e) =>
                                    setSelectedOrder((prev) =>
                                        prev ? { ...prev, paid: e.target.checked } : null
                                    )}
                                color="primary"
                            />
                        }
                        label={t("Paid")}
                        />
            <TextField
                            margin="dense"
                            label={t("Invoice Number")}
                            fullWidth
                            value={selectedOrder?.invoiceNumber || ""}
                            onChange={(e) =>
                                setSelectedOrder((prev) =>
                                    prev ? { ...prev, invoiceNumber: e.target.value } : null
                                )}
                        />
            <TextField
                margin="dense"
                        label={t("Notes")}
                fullWidth
                value={selectedOrder?.notes || ""}
                onChange={(e) =>
                    setSelectedOrder((prev) =>
                        prev ? { ...prev, notes: e.target.value } : null
                    )}
                />
            <FormControlLabel
                        control={
                            <Checkbox
                                checked={selectedOrder?.documentStored || false}
                                onChange={(e) =>
                                    setSelectedOrder((prev) =>
                                        prev ? { ...prev, documentStored: e.target.checked } : null
                                    )}
                                color="primary"
                            />
                        }
                        label={t("Documents Stored")}
            />
            <TextField
                margin="dense"
                        label={t("Missing Info")}
                fullWidth
                value={selectedOrder?.missingInfo || ""}
                onChange={(e) =>
                    setSelectedOrder((prev) =>
                        prev ? { ...prev, missingInfo: e.target.value } : null
                    )}
            />

        </DialogContent>
        < DialogActions >
            <Button onClick={ handleClose } color = "secondary" >
                        {t("Cancel")}
                </Button>
            < Button onClick = { handleSave } color = "primary" >
                        {t("Save")}
            </Button>
        </DialogActions>
                </Dialog>

                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>

        </div>
    </LocalizationProvider>
    );
};

export default OrderTable;